import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    return localStorage.getItem('selectedProjectId') || null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data.user);
          setProjects(response.data.projects || []);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } catch (error) {
          console.error('Error validating token:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username, password) => {
    const response = await authAPI.login(username, password);
    const { token, user: userData, projects: userProjects } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setProjects(userProjects || []);

    // Auto-select first project if available
    if (userProjects && userProjects.length > 0) {
      const firstProjectId = userProjects[0].id.toString();
      setSelectedProjectId(firstProjectId);
      localStorage.setItem('selectedProjectId', firstProjectId);
    }

    return { user: userData, projects: userProjects };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedProjectId');
    setUser(null);
    setProjects([]);
    setSelectedProjectId(null);
  };

  const selectProject = (projectId) => {
    setSelectedProjectId(projectId);
    localStorage.setItem('selectedProjectId', projectId);
  };

  const refreshProjects = async () => {
    try {
      const response = await authAPI.getMe();
      setProjects(response.data.projects || []);
      return response.data.projects;
    } catch (error) {
      console.error('Error refreshing projects:', error);
      return projects;
    }
  };

  const selectedProject = projects.find(
    (p) => p.id.toString() === selectedProjectId
  );

  const isAdmin = user?.role === 'ADMIN';

  const value = {
    user,
    projects,
    selectedProjectId,
    selectedProject,
    loading,
    isAdmin,
    login,
    logout,
    selectProject,
    refreshProjects,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
